import _ from "lodash";
import fieldFormatter from "./fieldFormatter";

export const showTotalProp = (totalRow, propName, hasPercentage = false) => {
  if (!totalRow || !propName) return "-";

  // Support dot notation for nested properties
  const value = propName.split(".").reduce((obj, key) => obj?.[key], totalRow);

  if (value || value === 0) {
    const totalValue = Array.isArray(value) ? value[0] : value;
    return hasPercentage ? `${totalValue.toFixed(1)} %` : totalValue;
  }

  return "-";
};

export const sumTotal = ({ data, column }) =>
  _(data)
    .map(column.id)
    .map((rowValue) => {
      if (_.isNumber(rowValue)) return rowValue;
      if (Array.isArray(rowValue) && rowValue.length) {
        return rowValue.reduce((accumulated, row) => accumulated + row);
      }
      return 0;
    })
    .reduce((accumulated, row) => accumulated + row);

export const sumRoundedTotal = ({ data, column }) => {
  const result = Math.round(sumTotal({ data, column }) * 100) / 100;
  return Number.isNaN(result) ? "" : result;
};

export const sumTotalSecond = ({ data, column }) => {
  const result = Math.round(sumTotal({ data, column })) / 1000;
  return Number.isNaN(result) ? "" : result;
};

export const sumRoundedWithoutGroupTotal = ({ data, column }) => {
  const result = Math.round(sumTotal({ data, column }) * 100) / 100 / 2;
  return Number.isNaN(result) ? "" : result;
};

export const sumTotalConditional =
  (filter) =>
  ({ data, column }) =>
    sumRoundedTotal({
      data: filter(data),
      column,
    });

export const sumWithoutGroupTotal = ({ data, column }) =>
  sumTotal({ data, column }) / 2;

export const sumOfGroupTotals = ({ data, column }) =>
  data && data.length > 0
    ? _(data)
        .map(column.id)
        .map((rowValue) => {
          if (_.isNumber(rowValue)) return rowValue;
          if (
            Array.isArray(rowValue) &&
            rowValue.length &&
            rowValue.length > 0
          ) {
            return rowValue
              .slice(0, 1)
              .reduce((accumulated, row) => accumulated + row);
          }
          return 0;
        })
        .reduce((accumulated, row) => accumulated + row)
    : 0;

export const rowCount = ({ data, column }) =>
  _(data)
    .map(column.id)
    .map((rowValue) => (_.isNaN(rowValue) || _.isEmpty(rowValue) ? 0 : 1))
    .reduce((accumulated, row) => accumulated + row);

export const sumTotalYesRow = ({ data, column }) =>
  _(data)
    .map(column.id)
    .map((rowValue) => (_.isEqual(rowValue, "Y") ? 1 : 0))
    .reduce((accumulated, row) => accumulated + row);

export const average = ({ data, column }) =>
  _(data)
    .map(column.id)
    .map((rowValue) => (_.isNumber(rowValue) ? rowValue : 0))
    .reduce((accumulated, row) => accumulated + row) / data.length;

export const averagePercentage = ({ data, column }) => {
  const percentageValue = average({ data, column });
  const roundedPercentageValue = (
    Math.round(percentageValue * 10) / 10
  ).toFixed(1);
  return `${roundedPercentageValue} %`;
};

export const preCalculatedTotal =
  (options) =>
  ({ data }) => {
    const { rowId, columnIds, transformer } = options;
    const { rowIdColumn, totalColumnId } = columnIds;
    const totalsRow = data.find(
      (row) => row[rowIdColumn].toLowerCase() === rowId
    );
    if (!totalsRow) return "";
    const value = totalsRow[totalColumnId];
    if (!value) return "";
    return transformer(totalsRow[totalColumnId]);
  };

export const totalRowRatio =
  (columnIds, showPercentage = true) =>
  ({ data }) => {
    const totals = columnIds.map((columnId) =>
      sumTotal({ data, column: { id: columnId } })
    );
    const percentageValue = ((totals[0] || 0) / totals[1]) * 100;
    const roundedPercentageValue = Math.round(percentageValue * 10) / 10;
    return `${
      Number.isNaN(roundedPercentageValue) ? 0 : roundedPercentageValue
    } ${showPercentage ? "%" : ""}`;
  };

export const filteredTotalRowRatio =
  (columnIds, showPercentage = true) =>
  ({ data }) => {
    if (columnIds.length < 2) return `0${showPercentage ? " %" : ""}`;

    const [numeratorKey, denominatorKey] = columnIds;

    const result = data
      .filter((row) => {
        const numerator = _.get(row, numeratorKey);
        const denominator = _.get(row, denominatorKey);
        return _.isFinite(numerator) && _.isFinite(denominator);
      })
      .reduce(
        (acc, curr) => {
          const numerator = _.get(curr, numeratorKey, 0);
          const denominator = _.get(curr, denominatorKey, 0);
          acc.numeratorTotal += numerator;
          acc.denominatorTotal += denominator;
          return acc;
        },
        {
          numeratorTotal: 0,
          denominatorTotal: 0,
        }
      );

    const { numeratorTotal, denominatorTotal } = result;

    const percentageValue = (numeratorTotal / (denominatorTotal || 1)) * 100;
    const roundedPercentageValue = Math.round(percentageValue * 10) / 10;

    return `${
      Number.isNaN(roundedPercentageValue) ? 0 : roundedPercentageValue
    }${showPercentage ? " %" : ""}`;
  };

export const totalRowPercentageWithGrandTotals =
  (columnIds) =>
  ({ data }) => {
    const totals = columnIds.map((columnId) =>
      sumOfGroupTotals({ data, column: { id: columnId } })
    );
    const percentageValue = ((totals[0] || 0) / totals[1]) * 100;
    const roundedPercentageValue = (
      Math.round(percentageValue * 10) / 10
    ).toFixed(1);
    return `${roundedPercentageValue} %`;
  };

export const totalRowRatioConditional =
  (columns) =>
  ({ data }) => {
    const totals = columns.map((column) =>
      sumRoundedTotal({
        data: column.filter ? column.filter(data) : data,
        column,
      })
    );
    const percentageValue = ((totals[0] || 0) / totals[1]) * 100;
    const roundedPercentageValue = (
      Math.round(percentageValue * 10) / 10
    ).toFixed(1);
    return `${roundedPercentageValue} %`;
  };

export function calculatorWithFormatter(formatter, calculator) {
  return ({ data, column }) => {
    const value = calculator({ data, column });
    return value === "" || Number.isNaN(value) ? "" : formatter({ value });
  };
}

export function formattedFloatSumTotal({ data, column }) {
  const formatter = fieldFormatter("float");
  return calculatorWithFormatter(formatter, sumRoundedTotal)({ data, column });
}

export const longestDuration = ({ data, column }) => {
  const result = _(data)
    .filter((row) => _.isString(row[column.id]) && row[column.id].length)
    .map((row) => {
      const hours = parseInt(_.first(row[column.id].split(":")), 10);
      const minutes = parseInt(_.last(row[column.id].split(":")), 10);

      return {
        rowValue: row[column.id],
        hours,
        minutes,
        formattedValue: `${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(
          2,
          "0"
        )}`,
      };
    })
    .orderBy(["hours", "minutes"], ["desc", "desc"])
    .head();
  return !_.isUndefined(result) ? result.formattedValue : "";
};
